import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import OrnateFrame from './OrnateFrame';

/**
 * 4-step welcome modal shown once after onboarding. The Onboarding screen
 * sets `localStorage.tour_pending = '1'` on completion; Home consumes it,
 * sets `tour_seen = '1'`, and mounts this component.
 */

const STEPS = [
  { icon: '✦', key: 'home' },
  { icon: '☼', key: 'card' },
  { icon: '✷', key: 'spreads' },
  { icon: '☾', key: 'diary' },
] as const;

export default function WelcomeTour({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);

  const last = step === STEPS.length - 1;
  const current = STEPS[step];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-night-900/80 px-5 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-sm"
      >
        <OrnateFrame className="rounded-3xl bg-night-800/90 p-6">
          {/* Progress dots */}
          <div className="mb-5 flex items-center justify-center gap-2">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-8 bg-gold-400' : 'w-3 bg-gold-500/30'
                }`}
              />
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={current.key}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.25 }}
              className="min-h-[200px] text-center"
            >
              <div className="mb-3 text-5xl text-gold-400 float-soft">
                {current.icon}
              </div>
              <h3 className="mb-3 font-serif text-xl text-gold-200">
                {t(`tour.step_${current.key}_title`)}
              </h3>
              <p className="px-1 font-serif text-sm leading-relaxed text-gold-100/80">
                {t(`tour.step_${current.key}_body`)}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-xs uppercase tracking-widest text-gold-300/60 hover:text-gold-200"
            >
              {t('tour.skip')}
            </button>
            <button
              type="button"
              onClick={() => (last ? onClose() : setStep((s) => s + 1))}
              className="btn-primary px-6"
            >
              {last ? t('tour.done') : t('tour.next')}
            </button>
          </div>
        </OrnateFrame>
      </motion.div>
    </div>
  );
}
